import { useAtom } from 'jotai';
import { CsvValidationException } from '@/components/Analysis/Idms/types';
import { BulkUploadPoisInput } from '@/__generated__/graphql';
import { atomWithReducer } from 'jotai/utils';

const getModalType = (issue: CsvValidationException['issue']) => {
  if (issue.type === 'header') {
    return 'invalid_format';
  } else if (issue.type === 'filetype') {
    return 'invalid_format';
  } else {
    return 'validation';
  }
};

type PoiBulkUploadState = {
  modal: 'closed' | 'upload' | 'confirm' | 'invalid_format' | 'invalid_data' | 'validation';
  file: File | null;
  input: BulkUploadPoisInput | undefined;
  errors: CsvValidationException | undefined;
};

export enum PoiBulkUploadActionTypes {
  CLOSE_ALL_MODALS = 'CLOSE_ALL_MODALS',
  PROCEED_TO_UPLOAD_CONFIRMATION = 'PROCEED_TO_UPLOAD_CONFIRMATION',
  CANCEL_CONFIRMATION = 'CANCEL_CONFIRMATION',
  OPEN_ERROR_MODAL = 'OPEN_ERROR_MODAL',
  OPEN_UPLOAD_MODAL = 'OPEN_UPLOAD_MODAL',
  POPULATE_UPLOAD_INPUT = 'POPULATE_UPLOAD_INPUT',
  DONE_SUCCESSFULLY_UPLOADING = 'DONE_SUCCESSFULLY_UPLOADING',
}

type PoiBulkUploadActionsWithPayloads =
  | {
      type: PoiBulkUploadActionTypes.CLOSE_ALL_MODALS;
    }
  | {
      type: PoiBulkUploadActionTypes.PROCEED_TO_UPLOAD_CONFIRMATION;
      payload: BulkUploadPoisInput;
    }
  | {
      type: PoiBulkUploadActionTypes.CANCEL_CONFIRMATION;
    }
  | {
      type: PoiBulkUploadActionTypes.OPEN_ERROR_MODAL;
      payload: CsvValidationException;
    }
  | {
      type: PoiBulkUploadActionTypes.OPEN_UPLOAD_MODAL;
    }
  | {
      type: PoiBulkUploadActionTypes.POPULATE_UPLOAD_INPUT;
      payload: BulkUploadPoisInput;
    }
  | {
      type: PoiBulkUploadActionTypes.DONE_SUCCESSFULLY_UPLOADING;
    };

const poiBulkUploadReducer = (
  prev: PoiBulkUploadState,
  action: PoiBulkUploadActionsWithPayloads
): PoiBulkUploadState => {
  switch (action.type) {
    case PoiBulkUploadActionTypes.CLOSE_ALL_MODALS:
      return {
        ...prev,
        modal: 'closed',
        file: null,
        input: undefined,
        errors: undefined,
      };
    case PoiBulkUploadActionTypes.PROCEED_TO_UPLOAD_CONFIRMATION:
      return {
        ...prev,
        input: action.payload,
        modal: 'confirm',
      };
    case PoiBulkUploadActionTypes.CANCEL_CONFIRMATION:
      return {
        ...prev,
        modal: 'closed',
      };
    case PoiBulkUploadActionTypes.OPEN_ERROR_MODAL:
      return {
        ...prev,
        errors: action.payload,
        modal: getModalType(action.payload.issue),
      };
    case PoiBulkUploadActionTypes.OPEN_UPLOAD_MODAL:
      return {
        ...prev,
        modal: 'upload',
      };
    case PoiBulkUploadActionTypes.POPULATE_UPLOAD_INPUT:
      return {
        ...prev,
        input: action.payload,
      };
    case PoiBulkUploadActionTypes.DONE_SUCCESSFULLY_UPLOADING:
      return {
        modal: 'closed',
        file: null,
        input: undefined,
        errors: undefined,
      };
    default:
      return prev;
  }
};

const poiBulkUploadState = atomWithReducer<PoiBulkUploadState, PoiBulkUploadActionsWithPayloads>(
  {
    modal: 'closed',
    errors: undefined,
    input: undefined,
    file: null,
  },
  poiBulkUploadReducer
);

export const useBulkUploadPoiActions = () => {
  const [state, dispatch] = useAtom(poiBulkUploadState);

  return { state, dispatch };
};
